import { useTranslation } from "react-i18next";
import RangeDatePicker from "common/components/DatePicker/RangePicker";
import React, { useEffect, useState } from "react";
import { Plus, XLg } from "react-bootstrap-icons";
import { getFormattedDate } from "components/Messages/utils";
import { FilterFields } from "./types";
import { tableColumns, typeOfFilter } from "../types";
import { GetSelectOptionsByType } from "../utils";

type Props = {
  columns: tableColumns[];
  submitFilter: (filters: FilterFields[]) => void;
  setIsFilterVisible: React.Dispatch<React.SetStateAction<boolean>>;
  filterValues: FilterFields[];
};

const CustomFilter = ({
  columns,
  submitFilter,
  setIsFilterVisible,
  filterValues,
}: Props) => {
  const { t } = useTranslation();
  const [filterCriteria, setFilterCriteria] = useState<FilterFields[]>(
    () => filterValues || []
  );
  const [thereIsInitialFilters, setThereIsInitialFilters] = useState(false);
  const selectOptions = GetSelectOptionsByType();

  useEffect(() => {
    if (filterValues.length !== 0) {
      setThereIsInitialFilters(true);
    }
  }, [filterValues]);

  const handleRootClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent click event from propagating
  };

  const handleAddFilter = () => {
    setFilterCriteria([...filterCriteria, { field: "", value: "" }]);
  };

  const handleRemoveFilter = (index) => {
    if (index === 0 && filterCriteria.length === 1) {
      handleClearFilter();
    } else {
      setFilterCriteria((prevCriteria) => {
        const updatedCriteria = [...prevCriteria];
        updatedCriteria.splice(index, 1);
        return updatedCriteria;
      });
    }
  };

  const handleColumnChange = (index, value) => {
    const column = columns.find((col) => col.accessor === value);
    const filterType = column?.type || null;

    setFilterCriteria((prevCriteria) => {
      const updatedCriteria = [...prevCriteria];
      updatedCriteria[index].field = value;

      if (filterType === typeOfFilter.date) {
        delete updatedCriteria[index].value;
        updatedCriteria[index].startDate = getFormattedDate(
          new Date().toString()
        );
        updatedCriteria[index].endDate = getFormattedDate(
          new Date().toString()
        );
      } else {
        updatedCriteria[index].value =
          filterType === typeOfFilter.indication ? true : "";
        delete updatedCriteria[index].startDate;
        delete updatedCriteria[index].endDate;
      }
      return updatedCriteria;
    });
  };

  const handleValueChange = (index: number, value: string | boolean) => {
    setFilterCriteria((prevCriteria) => {
      const updatedCriteria = [...prevCriteria];
      updatedCriteria[index].value = value;
      return updatedCriteria;
    });
  };

  const handleDateChange = (
    index: number,
    date: Date,
    isStartDate: boolean
  ) => {
    setFilterCriteria((prevCriteria) => {
      const updatedCriteria = [...prevCriteria];
      const onlyDate = getFormattedDate(date.toString());

      if (isStartDate) {
        updatedCriteria[index].startDate = onlyDate;
      } else {
        updatedCriteria[index].endDate = onlyDate;
      }
      return updatedCriteria;
    });
  };

  const handleSubmitFilter = () => {
    const onlyFiiledFilters: FilterFields[] = filterCriteria.filter(
      (criteria) =>
        (criteria.field && criteria.value !== "") ||
        (criteria.field && criteria.startDate && criteria.endDate)
    );

    setIsFilterVisible(false);
    submitFilter(onlyFiiledFilters);
  };

  const handleClearFilter = () => {
    setFilterCriteria([]);
    submitFilter([]);
    setIsFilterVisible(false);
  };

  const getDateFromFormattedDate = (formattedDateString: string) => {
    const [day, month, year] = formattedDateString.split("/");
    const convertedDate = new Date(`${year}-${month}-${day}`);
    return convertedDate;
  };

  const getFilterInputByType = (criteria: FilterFields, index: number) => {
    const column = columns.find((col) => col.accessor === criteria.field);
    const filterType = column?.type || null;
    let filterContent;

    if (filterType === typeOfFilter.date) {
      filterContent = (
        <RangeDatePicker
          initialStartDate={
            criteria?.startDate
              ? getDateFromFormattedDate(criteria.startDate)
              : new Date()
          }
          initialEndDate={
            criteria?.endDate
              ? getDateFromFormattedDate(criteria.endDate)
              : new Date()
          }
          onStartDateChange={(date) => handleDateChange(index, date, true)}
          onEndDateChange={(date) => handleDateChange(index, date, false)}
        />
      );
    } else if (filterType === typeOfFilter.indication) {
      filterContent = (
        <select
          className="true-false-select-filter"
          value={criteria.value.toString()}
          onChange={(e) => handleValueChange(index, e.target.value === "true")}
        >
          <option key="yes" value="true">
            {t("yes")}
          </option>
          <option key="no" value="false">
            {t("no")}
          </option>
        </select>
      );
    } else if (
      [
        typeOfFilter.statusSelect,
        typeOfFilter.offenceSelect,
        typeOfFilter.tagsSelect,
        typeOfFilter.decisionSelect,
        typeOfFilter.rejectionReasonSelect,
      ].includes(filterType)
    ) {
      filterContent = (
        <select
          // className="list-select-filter"
          value={criteria.value.toString()}
          onChange={(e) => handleValueChange(index, e.target.value)}
          required
        >
          <option value="" disabled>
            {t("select")}
          </option>
          {selectOptions[filterType].map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    } else {
      filterContent = (
        <input
          type="text"
          className="input-filter"
          value={criteria.value.toString()}
          onChange={(e) => handleValueChange(index, e.target.value)}
          placeholder={t("enterValue")}
        />
      );
    }

    return filterContent;
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="filters-popup" onClick={handleRootClick}>
      <button
        type="button"
        className="add-filter-button"
        onClick={handleAddFilter}
        disabled={filterCriteria.length === columns.length}
      >
        <Plus size={24} />
        {t("addFilter")}
      </button>
      {filterCriteria.map((criteria: FilterFields, index: number) => {
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${criteria.field}-${index}`} className="filters-container">
            <button
              type="button"
              className="remove-filter-button"
              onClick={() => handleRemoveFilter(index)}
            >
              <XLg size={18} />
            </button>
            <select
              className="select-filter"
              value={criteria.field}
              onChange={(e) => handleColumnChange(index, e.target.value)}
            >
              <option value="" disabled>
                {t("select")}
              </option>
              {columns.map((column) => (
                <option
                  key={column.accessor}
                  value={column.accessor}
                  disabled={filterCriteria.some(
                    (filter) => filter.field === column.accessor
                  )}
                >
                  {column.Header}
                </option>
              ))}
            </select>
            {getFilterInputByType(criteria, index)}
          </div>
        );
      })}
      <div className="filter-buttons">
        {filterCriteria.length > 0 && (
          <button
            type="button"
            className="submit-filter-button"
            onClick={handleSubmitFilter}
          >
            {t("filter")}
          </button>
        )}
        {filterCriteria.length > 0 && thereIsInitialFilters && (
          <button
            type="button"
            className="clear-filter-button"
            onClick={handleClearFilter}
          >
            {t("clearFilter")}
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomFilter;
